import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useGTMBasicPageViewEventWithProperties } from '../../../helpers/gtmHelper'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { s_blog } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { STUDENT_PAGE_VIEW_ID } from '../../../modules/ids/gtm/student'
import client from '../../../modules/sanityClient'
import { COLORS } from '../../../modules/vars'
import { getBlogHomeGroqQueryAndParams } from '../../../sanity/groqQueries'
import { BLOG_HOME_QUERY_MAIN_MENUResult } from '../../../sanity/types'
import MobileLandingNav from '../navigation/LandingNav/MobileLandingNav'
import Footer from '../section/footer'
import BlogMobileCategorySection from './BlogMobileCategorySection'
import BlogMobileMainPostSection from './BlogMobileMainPostSection'
import BlogMobileSubCategorySection from './BlogMobileSubCategorySection'
import BlogPostListSection from './BlogPostListSection'
import BlogSearchTab from './BlogSearchTab'
import { MENU_ID } from './type'

const BlogMobileTitleSection = ({ isPortal }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const QUERY_BLOG_TYPE = 'blog'
  const isBlog = router.query.type === QUERY_BLOG_TYPE
  const isWebview = useIsWebview(router.asPath.includes('/webview/'))
  return (
    <>
      {!isWebview && (
        <h1 className={style.topTitleSectionTitle}>
          {isPortal || isBlog ? t(s_blog.title.blog) : t(s_blog.title.help_center)}
        </h1>
      )}
    </>
  )
}

const TYPE = {
  BLOG: 'blog',
  FAQ: 'question',
  NOTICE: 'notice',
}

function selectTargetForm(items, target) {
  return items.filter((item) => item.form === target)
}

const HelpcenterBanner = () => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-start gap-[40px] bg-gray-50 px-[16px] py-[64px] text-gray-700">
      <div className="max-w-[288px]">
        <div className="Headline2-24Bd break-keep">{t(s_blog.section_contact_us.title)}</div>
        <div className="mt-[24px] w-[160px]">
          <DSButton
            onClick={() => window.ChannelIO('openChat')}
            type={BUTTON_TYPE.SOLID}
            size={BUTTON_SIZE.MD}
            customBgColor={COLORS.GRAY100}
          >
            <DSButton.Label customTxtColor={COLORS.GRAY700}>{t(s_blog.section_contact_us.btn)}</DSButton.Label>
          </DSButton>
        </div>
      </div>
      <div className="text-gray-600">
        <div className="Caption-12Md">{t(s_blog.section_contact_us.caption_1)}</div>
        <div className="Headline2-24Md my-[8px]">{t(s_blog.section_contact_us.number)}</div>
        <div className="Caption-12Md mt-[8px] mb-[4px]">{t(s_blog.section_contact_us.caption_2)}</div>
        <div className="Caption-12Bd">{t(s_blog.section_contact_us.desc_1)}</div>
        <div className="Caption-12Md">{t(s_blog.section_contact_us.desc_2)}</div>
      </div>
    </div>
  )
}

const cacheKey = (start, end, selectedMenuId, selectedSubMenuId, lang) => [
  'posts',
  selectedMenuId,
  selectedSubMenuId,
  start,
  end,
  lang,
]

const fetchFunction = async (start, end, selectedMenuId, selectedSubMenuId, lang) => {
  const { query, params } = getBlogHomeGroqQueryAndParams(start, end, selectedMenuId, selectedSubMenuId, lang)

  const posts: BLOG_HOME_QUERY_MAIN_MENUResult[] = await client.fetch(query, params)

  return posts
}

interface IBlogHomeMobile {
  mainPost
  posts
  menu
  subMenu
  isPortal?
  isWebviewInitialValue?
}
const BlogHomeMobile = ({
  mainPost,
  menu = [],
  subMenu,
  isPortal = false,
  isWebviewInitialValue = false,
}: IBlogHomeMobile) => {
  const { query, asPath, push } = useRouter()
  const isWebview = useIsWebview(isWebviewInitialValue)
  const { t, isLocaleEN, isLocaleKO } = useTranslation()
  const CUSTOM_MENU_ID = 0
  const BLOG_HOME_MENU_OBJ = { title: t(s_blog.category.home), _id: CUSTOM_MENU_ID, form: MENU_ID.BLOG, index: 1 }
  const ALL_SUB_MENU_OBJ = {
    title: t(s_blog.search_reult.cateory_1),
    _id: CUSTOM_MENU_ID,
    menuCategory: { _ref: CUSTOM_MENU_ID },
  }

  const getFirstSelectedMenu = (type, menu) => {
    const hash = asPath.split('#')[1] || 0
    if (hash) {
      return categoryList[hash]
    }

    const RINGLE_GUIDE_ID_LIST = [
      'c7cb2ce8-d08c-4e87-a1da-df0702c925c9',
      '16f44e8b-a228-484a-a3a2-a3fd1507d8d9',
      'ecb657fd-6ae3-471c-a044-bc99071e51b3',
      '1714ffa3-010e-4b90-be10-8b0e055645c8',
      '8b29cec2-1bdb-4c8c-b70b-9a5bb39b15e0',
    ]

    switch (type) {
      case TYPE.FAQ:
        return menu.filter((item) => RINGLE_GUIDE_ID_LIST.includes(item._id))[0]
      case TYPE.NOTICE:
        return selectTargetForm(menu, TYPE.NOTICE)[0]
      default:
        return categoryList[0]
    }
  }

  //메인 카테고리 리스트
  const categoryList = [BLOG_HOME_MENU_OBJ, ...menu] // 블로그 홈 + ...메뉴:

  // URL 파라미터에서 카테고리 ID 가져오기
  const getSelectedMenuFromQuery = () => {
    const categoryId = query.category
    if (!categoryId) return getFirstSelectedMenu(query.type, menu)
    return categoryList.find((item) => item._id === categoryId) || getFirstSelectedMenu(query.type, menu)
  }

  // URL 파라미터에서 서브카테고리 ID 가져오기
  const getSelectedSubMenuFromQuery = () => {
    const subcategoryId = query.subcategory
    if (!subcategoryId) return ALL_SUB_MENU_OBJ
    return subMenu?.find((item) => item._id === subcategoryId) || ALL_SUB_MENU_OBJ
  }

  // URL 파라미터 업데이트
  const updateUrlParams = (categoryId, subcategoryId = null) => {
    const newQuery: { [key: string]: any } = {
      ...query,
      category: categoryId,
    }

    // 서브카테고리가 있을 때만 추가
    if (subcategoryId) {
      newQuery.subcategory = subcategoryId
    } else {
      // 서브카테고리 파라미터 제거
      delete newQuery.subcategory
    }

    push(
      {
        pathname: asPath.split('?')[0],
        query: newQuery,
      },
      undefined,
      { shallow: true }
    )
  }

  // 메인 카테고리 변경 핸들러
  const handleMenuChange = (menu) => {
    setSelectedMenu(menu)
    setSelectedSubmenu(ALL_SUB_MENU_OBJ)
    // 카테고리만 변경하고 서브카테고리는 제거
    updateUrlParams(menu._id)
  }

  // 서브카테고리 변경 핸들러
  const handleSubMenuChange = (subMenu) => {
    setSelectedSubmenu(subMenu)
    updateUrlParams(selectedMenu._id, subMenu._id)
  }

  const [selectedMenu, setSelectedMenu] = useState(getSelectedMenuFromQuery())
  const [selectedSubMenu, setSelectedSubmenu] = useState(getSelectedSubMenuFromQuery())

  const isShowHelpCenterBanner =
    (isLocaleKO || isLocaleEN) && (selectedMenu?.form == MENU_ID.FAQ || selectedMenu?.form == MENU_ID.NOTICE)

  //서브 카테고리 리스트
  const subMenuList = useMemo(() => {
    return subMenu?.filter((subItem) => subItem.menu === selectedMenu?.title) || []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMenu, selectedMenu])

  const subCategoryList = [ALL_SUB_MENU_OBJ, ...subMenuList] // 전체 + ...메뉴

  const isBlogHome = selectedMenu?._id === CUSTOM_MENU_ID

  const sendGTMPVEvent = useGTMBasicPageViewEventWithProperties({
    page_id: STUDENT_PAGE_VIEW_ID.LANDING_BLOG,
    page_title: asPath,
    device_type: 'mobile_web',
  })

  useEffect(() => {
    sendGTMPVEvent()
  }, [])

  // URL 파라미터 변경 감지
  useEffect(() => {
    const newSelectedMenu = getSelectedMenuFromQuery()
    const newSelectedSubMenu = getSelectedSubMenuFromQuery()

    if (newSelectedMenu._id !== selectedMenu._id) {
      setSelectedMenu(newSelectedMenu)
    }
    if (newSelectedSubMenu._id !== selectedSubMenu._id) {
      setSelectedSubmenu(newSelectedSubMenu)
    }
  }, [query.category, query.subcategory])

  return (
    <>
      {!isPortal && !isWebview && query.app !== 'true' && <MobileLandingNav />}
      <div className={classNames(style.bodyWrapper, isPortal ? 'mt-[60px]' : 'mt-[20px]', isWebview && 'mb-[60px]')}>
        <BlogMobileTitleSection isPortal={isPortal} />
        <div className="relative px-[16px]">
          <BlogSearchTab isMobile={true} />
        </div>
        <BlogMobileCategorySection categoryList={categoryList} selected={selectedMenu} setSelected={handleMenuChange} />
        {isBlogHome ? (
          <BlogMobileMainPostSection thumbnail={mainPost[0].thumbnail} mainPost={mainPost[0].mainPost} />
        ) : (
          <>
            {' '}
            {selectedMenu?.mainPost && (
              <BlogMobileMainPostSection
                thumbnail={selectedMenu?.mainPost?.thumbnail}
                mainPost={selectedMenu?.mainPost}
              />
            )}
            <BlogMobileSubCategorySection
              subMenu={subCategoryList}
              selected={selectedSubMenu}
              setSelected={handleSubMenuChange}
            />
          </>
        )}
        <BlogPostListSection
          isMobile={true}
          menuId={selectedMenu?._id}
          subMenuId={selectedSubMenu._id}
          menuForm={selectedMenu?.form}
        />
      </div>
      {isShowHelpCenterBanner && <HelpcenterBanner />}

      {!isPortal && !isWebview && <Footer />}
    </>
  )
}

export default BlogHomeMobile

const style = {
  bodyWrapper: `relative mx-auto flex flex-col text-black`,
  topTitleSectionTitle: `Headline1-28Bd flex px-[20px]`,
  topTitleSectionSubtitle: `Headline2-24Md text-gray-700`,
}
